import { memo } from 'react';

import { Checkbox } from '@/shadcn/checkbox';
import { cn } from '@/shadcn/lib/utils';
import { Skeleton } from '@/shadcn/skeleton';

import { AvatarComponent } from '@/components/ui/avatar/avatar';
import type { LoadingProps } from '@/types/loadingProps';
import { TestId } from '@/types/testId';
import { pluralize } from '@/utils';

type Props = LoadingProps<{
  accountId: number;
  name: string;
  avatar: string | null | undefined;
  nbParticipants: number;
  isSelected: boolean;
  toggleTeam: (accountId: number) => void;
}>;
// A single clickable team row
export const TeamItem = memo((props: Props) => {
  if (props.loading) return <TeamItemSkeleton />;

  const toggleThisTeam = () => {
    props.toggleTeam(props.accountId);
  };

  return (
    <label
      className={cn(
        'flex cursor-pointer flex-row flex-nowrap items-center justify-between rounded-md border border-transparent bg-card px-2 py-1.5 transition-[border-color] hover:bg-accent',
        props.isSelected && 'border-primary bg-accent'
      )}
      data-cy={TestId.TeamItem.ToggleTeamName}
      htmlFor={`team-item-${props.accountId}`}
      key={props.accountId}
    >
      <div className="flex grow flex-row flex-nowrap items-center justify-start gap-3">
        <AvatarComponent name={props.name} source={props.avatar} />

        <div className="flex flex-col flex-nowrap items-start overflow-hidden text-sm capitalize">
          {props.name}
          <div className="text-muted-foreground text-xs">
            {pluralize(props.nbParticipants, 'participant')}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-row flex-nowrap items-center">
        <Checkbox
          aria-label={`Add the ${props.name} team to the group.`}
          checked={props.isSelected}
          data-testid={TestId.TeamItem.ToggleTeamCheckbox}
          id={`team-item-${props.accountId}`}
          onCheckedChange={toggleThisTeam}
        />
      </div>
    </label>
  );
});

const TeamItemSkeleton = () => (
  <div className="flex flex-row flex-nowrap items-center justify-between rounded-md border border-transparent px-2 py-2">
    <div className="flex grow flex-row flex-nowrap items-center justify-start gap-3">
      <Skeleton className="h-8 w-8 rounded-full" />

      <div className="flex flex-col flex-nowrap items-start gap-1 overflow-hidden text-sm capitalize">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
    <div className="flex shrink-0 flex-row flex-nowrap items-center">
      <Skeleton className="size-4 rounded-md" />
    </div>
  </div>
);
